import { useCallback, useEffect, useRef } from 'react';
import { useAuth0, LogoutOptions } from '@auth0/auth0-react';
import { isLogoutInProgress, markLogoutInProgress, ssoLogout } from '../auth/logout';
import { decodeJwtExpMs, onTokenObserved, onSessionInvalid } from '../auth/tokenExpiry';

// Auth0 error codes that require re-login
const SESSION_INVALID_CODES = [
  'login_required',
  'consent_required',
  'interaction_required',
  'invalid_grant',
] as const;

// Force logout this many ms before the access token's `exp`, so we don't keep a
// token around that the API would reject as expired in the meantime.
const EXPIRY_SKEW_MS = 5000;

// setTimeout delays larger than this overflow a 32-bit int and fire immediately.
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

const SESSION_INVALID_PATTERNS = [
  'login required',
  'missing refresh token',
  'invalid refresh token',
  'refresh token expired',
  'unknown or invalid refresh token',
];

function isSessionInvalidError(error: unknown): boolean {
  if (!error) return false;

  // Check error code (Auth0 SDK typically includes this)
  const errorObj = error as { error?: string; code?: string; message?: string; error_description?: string };
  const errorCode = errorObj.error || errorObj.code || '';

  if (SESSION_INVALID_CODES.includes(errorCode as typeof SESSION_INVALID_CODES[number])) {
    return true;
  }

  // Check error message for common patterns
  const errorMessage = String(errorObj.message || errorObj.error_description || '').toLowerCase();
  return SESSION_INVALID_PATTERNS.some(pattern => errorMessage.includes(pattern));
}

export interface UseAuthMonitorOptions {
  onSessionInvalid?: () => void;
}

/**
 * Monitors the Auth0 session and forces a logout when the access token expires.
 * The logout is scheduled from the token's `exp` claim and re-armed whenever a
 * refreshed token is observed (via the global token provider), so an active
 * user's session is extended while an idle one is logged out at expiry.
 */
export function useAuthMonitor(options?: UseAuthMonitorOptions) {
  const { error, isAuthenticated, isLoading, logout, getAccessTokenSilently } = useAuth0();

  const isLoggingOut = useRef(false);
  const expiryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const triggerLogout = useCallback(() => {
    if (isLoggingOut.current || isLogoutInProgress()) return;
    isLoggingOut.current = true;
    markLogoutInProgress();

    console.warn('[Auth] Session invalid, logging out');
    options?.onSessionInvalid?.();

    const logoutOptions: LogoutOptions = {
      logoutParams: {
        returnTo: typeof window !== 'undefined' ? window.location.origin : undefined,
      },
    };

    logout(logoutOptions).catch(() => {
      isLoggingOut.current = false;
      ssoLogout();
    });
  }, [logout, options]);

  // (Re)schedule the logout for a given token expiry. A null/past expiry logs
  // out immediately; re-arming to the same exp is idempotent.
  const armFromExp = useCallback((expMs: number | null) => {
    if (expiryTimer.current) { clearTimeout(expiryTimer.current); expiryTimer.current = null; }
    if (!expMs) return;

    const delay = expMs - Date.now() - EXPIRY_SKEW_MS;
    if (delay <= 0) {
      triggerLogout();
      return;
    }
    expiryTimer.current = setTimeout(triggerLogout, Math.min(delay, MAX_TIMEOUT_MS));
  }, [triggerLogout]);

  // Arm from the current token, then re-arm whenever a refreshed token is
  // observed by the global token provider during API activity.
  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      if (expiryTimer.current) { clearTimeout(expiryTimer.current); expiryTimer.current = null; }
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        // Returns the cached token while it is still valid, so reading the exp
        // here does not itself trigger a refresh.
        const token = await getAccessTokenSilently();
        if (cancelled || !token) return;
        armFromExp(decodeJwtExpMs(token));
      } catch {
        // Token retrieval failures surface via the `error` effect below.
      }
    })();

    const unsubscribe = onTokenObserved((expMs) => {
      if (!cancelled) armFromExp(expMs);
    });

    return () => {
      cancelled = true;
      unsubscribe();
      if (expiryTimer.current) { clearTimeout(expiryTimer.current); expiryTimer.current = null; }
    };
  }, [isAuthenticated, isLoading, getAccessTokenSilently, armFromExp]);

  useEffect(() => {
    if (error && isSessionInvalidError(error)) {
      triggerLogout();
    }
  }, [error, triggerLogout]);

  // Log out when the token endpoint rejects a refresh with 403.
  useEffect(() => onSessionInvalid(triggerLogout), [triggerLogout]);

  return {
    isSessionValid: isAuthenticated && !error,
    isMonitoring: !isLoading,
  };
}

export default useAuthMonitor;
